import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, InboxIcon, HandIcon } from 'lucide-react';
import { useBusiness, getHubTabs, BUSINESS_TYPE_LABEL } from '../components/BusinessContext';
import { BusinessDashboard } from '../components/BusinessDashboard';
import { BusinessCatalog } from '../components/BusinessCatalog';
import { BusinessLeads } from '../components/BusinessLeads';
import { BusinessSettings } from '../components/BusinessSettings';
import { BusinessAIChannels } from '../components/BusinessAIAgents';
import { UpgradePlanModal } from '../components/UpgradePlanModal';
export function BusinessHub() {
  const {
    hub,
    markHubSeen,
    hasSeenTabsIntro,
    markTabsIntroSeen,
    trialActive,
    trialDaysRemaining,
    trialPlan,
    subscribed
  } = useBusiness();
  const tabs = getHubTabs(hub?.type ?? 'generic');
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  useEffect(() => {
    markHubSeen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!hub?.active) {
    return <div className="flex-1 flex items-center justify-center h-full bg-[#F8FAFC]" data-id="element-2350">
        <p className="text-slate-500" data-id="element-2351">No Business Hub yet.</p>
      </div>;
  }
  const businessName = hub.name || 'Your Business';
  return <>
      <div className="flex-1 flex flex-col h-full bg-[#F8FAFC]" data-id="element-2352">
        {/* Hub Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-100 px-4 pt-3" data-id="element-2353">
          <div className="w-full" data-id="element-2354">
            {/* Trial chip — only while on trial (pre-subscription) */}
            {trialActive && !subscribed && <div className="flex items-center gap-2 bg-primary-indigo/5 border border-primary-indigo/10 rounded-xl px-3 py-2 mb-3" data-id="element-2355">
                <SparklesIcon className="w-4 h-4 text-primary-indigo flex-shrink-0" data-id="element-2356" />
                <span className="text-[13px] font-semibold text-dark-navy" data-id="element-2357">
                  {trialPlan ? `${trialPlan} trial` : 'Free trial'}
                </span>
                <span className="text-[13px] text-primary-indigo font-medium ml-auto" data-id="element-2358">
                  {trialDaysRemaining} days left
                </span>
                <button onClick={() => setUpgradeOpen(true)} className="ml-2 px-2.5 py-1 text-xs font-bold rounded-lg active:scale-95 transition-transform flex-shrink-0 bg-primary-indigo text-white shadow-sm" data-id="element-2359">
                  Upgrade
                </button>
              </div>}

            {/* Tabs */}
            <div className="relative pb-3" data-id="element-2360">
              <div className="flex gap-2 overflow-x-auto hide-scrollbar" data-id="element-2361">
                {tabs.map(tab => {
                const isActive = tab === activeTab;
                return <button key={tab} onClick={() => setActiveTab(tab)} className={`relative px-4 py-2 text-sm font-semibold whitespace-nowrap rounded-full transition-colors shrink-0 ${isActive ? 'bg-dark-navy text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-2362">
                      {tab}
                    </button>;
              })}
              </div>

              {/* Coachmark */}
              {!hasSeenTabsIntro && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="absolute left-4 right-4 top-full mt-3 z-50 bg-dark-navy text-white p-4 rounded-2xl shadow-xl" data-id="element-2363">
                  <div className="absolute -top-2 right-12 w-4 h-4 bg-dark-navy rotate-45 rounded-sm" data-id="element-2364" />
                  <div className="relative flex items-start gap-3" data-id="element-2365">
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0" data-id="element-2366">
                      <motion.div animate={{
                    x: [-4, 4, -4]
                  }} transition={{
                    repeat: Infinity,
                    duration: 1.5,
                    ease: 'easeInOut'
                  }} data-id="element-2367">
                        <HandIcon className="w-5 h-5 text-electric-cyan" data-id="element-2368" />
                      </motion.div>
                    </div>
                    <div className="flex-1" data-id="element-2369">
                      <h3 className="font-semibold text-sm mb-1" data-id="element-2370">
                        Scroll for more modules
                      </h3>
                      <p className="text-xs text-white/70 mb-3 leading-relaxed" data-id="element-2371">
                        Swipe left on the menu above to access your Leads, AI
                        Agents, and Settings.
                      </p>
                      <button onClick={markTabsIntroSeen} className="w-full py-2 bg-white text-dark-navy text-sm font-bold rounded-xl active:scale-95 transition-transform" data-id="element-2372">
                        Got it
                      </button>
                    </div>
                  </div>
                </motion.div>}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-5" data-id="element-2373">
          <div className="w-full h-full" data-id="element-2374">
            {activeTab === 'Dashboard' ? <BusinessDashboard onNavigate={setActiveTab} data-id="element-2375" /> : activeTab === tabs[1] ? <BusinessCatalog data-id="element-2376" /> : activeTab === 'Leads' ? <BusinessLeads data-id="element-2377" /> : activeTab === 'AI Channels' ? <BusinessAIChannels data-id="element-2378" /> : activeTab === 'Settings' ? <BusinessSettings data-id="element-2379" /> : <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex flex-col items-center justify-center text-center py-24" data-id="element-2380">
                <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 flex items-center justify-center mb-5 shadow-sm" data-id="element-2381">
                  <InboxIcon className="w-8 h-8 text-slate-300" data-id="element-2382" />
                </div>
                <h2 className="font-display font-bold text-2xl text-dark-navy mb-2" data-id="element-2383">
                  No {activeTab.toLowerCase()} yet
                </h2>
                <p className="text-slate-500 max-w-md" data-id="element-2384">
                  This is where your {activeTab.toLowerCase()} will appear once
                  you set up your Business Hub. Ask Talab to help you get
                  started.
                </p>
              </motion.div>}
          </div>
        </div>
      </div>
      {upgradeOpen && <UpgradePlanModal onClose={() => setUpgradeOpen(false)} data-id="element-2385" />}
    </>;
}